import React, { useEffect, useRef, useState } from "react"
import { Backdrop, CircularProgress, Snackbar } from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import backdropStyles from '../AgentSkillsManagement/styles.module.css'
import { Pages } from "../../models/Pages"
import {
    ExemptDestination,
    IDestinationFilters,
} from "../../models/ExemptDestinations/ExemptDestination"
import { ExemptDestinationsActions } from "../../actions/ExemptDestinationsActions"
import ControlPanel from "./ControlPanel"
import DestinationsList from "./DestinationsList"
import AddDestination from "./AddDestination"
import CommentDialog, { DialogTone } from "../RobocallBlocks/CommentDialog"

/**
 * Exempt Destinations — our own inbound numbers that no spam filter may reject on.
 *
 * This list used to be a literal in IncomingCallHandler, so adding a DID to it meant a code
 * change, a review and a production deploy. It is a decision a call-center lead makes and
 * needs in force the same hour, which is the whole reason this screen exists.
 *
 * Attribution comes from the authenticated Apollo user server-side; every change here writes
 * a history row with the operator's comment, and the API refuses a change without one.
 */
const ExemptDestinations = () => {
    const [filters, setFilters] = useState<IDestinationFilters>({})
    const [destinations, setDestinations] = useState<ExemptDestination[]>([])
    const [pagination, setPagination] = useState<Pages | null>(null)
    const [currPage, setCurrPage] = useState(1)
    const [loading, setLoading] = useState('loaded')
    const [backdrop, setBackdrop] = useState(true)
    const [showAdd, setShowAdd] = useState(false)

    const [alert, setAlert] = useState<{ severity: 'error' | 'success', text: string } | null>(null)
    const [dialog, setDialog] = useState<{
        title: string
        description?: React.ReactNode
        commentRequired: boolean
        actionLabel: string
        tone: DialogTone
        submit: (comment: string) => void
    } | null>(null)

    const searchTimer = useRef<number | null>(null)
    // Filters live in state, but the fetch has to see the value that triggered it — a
    // debounced callback would otherwise close over a stale object.
    const filtersRef = useRef(filters)
    const pageRef = useRef(currPage)
    filtersRef.current = filters
    pageRef.current = currPage

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const describe = (error: any): string => {
        const data = error?.response?.data
        if (data?.error) {
            return data.error
        }
        if (data?.messages) {
            return Object.keys(data.messages).map((k) => data.messages[k].join(' ')).join(' ')
        }
        return error?.message || 'Request failed'
    }

    const load = () => {
        setLoading('loading')
        ExemptDestinationsActions.getDestinations(filtersRef.current, pageRef.current)
            .then((data: any) => {
                setDestinations(data.data || [])
                setPagination(new Pages(data.per_page, data.current_page, data.last_page))
                setLoading('loaded')
                setBackdrop(false)
            })
            .catch((error) => {
                setAlert({ severity: 'error', text: describe(error) })
                setLoading('loaded')
                setBackdrop(false)
            })
    }

    const handleFilters = (next: IDestinationFilters) => {
        const searchChanged = next.q !== filters.q
        setFilters(next)
        filtersRef.current = next
        setCurrPage(1)
        pageRef.current = 1

        if (searchTimer.current) {
            window.clearTimeout(searchTimer.current)
        }
        // Typing debounces; ticking the checkbox should feel immediate.
        searchTimer.current = window.setTimeout(load, searchChanged ? 800 : 0)
    }

    const handleChangePage = (page: number) => {
        setCurrPage(page)
        pageRef.current = page
        load()
    }

    const after = (message: string) => {
        setAlert({ severity: 'success', text: message })
        load()
    }

    const fail = (error: any) => setAlert({ severity: 'error', text: describe(error) })

    const edit = (destination: ExemptDestination) => setDialog({
        title: `Edit the reason for ${destination.phone_e164}`,
        description: <>The number stays exempt. Only the reason changes, and the previous one
            is kept in the history.</>,
        commentRequired: true,
        actionLabel: 'Save reason',
        tone: 'neutral',
        submit: (comment) => ExemptDestinationsActions.updateDestination(destination.id!, comment)
            .then(() => after('Reason updated.'))
            .catch(fail),
    })

    const remove = (destination: ExemptDestination) => setDialog({
        title: `Stop exempting ${destination.phone_e164}?`,
        description: <>Calls to this number go back under <b>every</b> inbound spam filter,
            starting with the next call. The entry and its history are kept, so it can be
            restored later.</>,
        commentRequired: true,
        actionLabel: 'Remove exemption',
        tone: 'clear',
        submit: (comment) => ExemptDestinationsActions.deleteDestination(destination.id!, comment)
            .then(() => after(`${destination.phone_e164} is no longer exempt.`))
            .catch(fail),
    })

    const restore = (destination: ExemptDestination) => setDialog({
        title: `Exempt ${destination.phone_e164} again?`,
        description: <>No filter will reject on it from the next call onwards. Its earlier
            history stays attached.</>,
        commentRequired: true,
        actionLabel: 'Restore exemption',
        tone: 'confirm',
        submit: (comment) => ExemptDestinationsActions.updateDestination(destination.id!, comment)
            .then(() => after(`${destination.phone_e164} is exempt again.`))
            .catch(fail),
    })

    return (
        <>
            <ControlPanel
                filters={filters}
                searchStatus={loading}
                onChange={handleFilters}
                onAdd={() => setShowAdd(true)}
            />

            <DestinationsList
                destinations={destinations}
                pagination={pagination}
                handleChangePage={handleChangePage}
                onEdit={edit}
                onRemove={remove}
                onRestore={restore}
            />

            <AddDestination
                show={showAdd}
                toggleHandle={() => setShowAdd(false)}
                onSubmit={(phone, comment) =>
                    ExemptDestinationsActions.createDestination(phone, comment)
                        .then(() => after(`${phone} is now exempt.`))
                        .catch(fail)}
            />

            {dialog !== null ?
                <CommentDialog
                    show={true}
                    title={dialog.title}
                    description={dialog.description}
                    commentRequired={dialog.commentRequired}
                    actionLabel={dialog.actionLabel}
                    tone={dialog.tone}
                    toggleHandle={() => setDialog(null)}
                    onSubmit={dialog.submit}
                />
                : null}

            <Snackbar
                open={alert !== null}
                autoHideDuration={5000}
                onClose={() => setAlert(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert onClose={() => setAlert(null)} severity={alert?.severity || 'error'} variant="filled">
                    <div className={backdropStyles.AlertError}>{alert?.text}</div>
                </Alert>
            </Snackbar>

            <Backdrop className={backdropStyles.Backdrop} open={backdrop}>
                <CircularProgress className={backdropStyles.BackdropProgress} />&nbsp;&nbsp;
                <p className={backdropStyles.BackdropProgress}>Loading...</p>
            </Backdrop>
        </>
    )
}

export default ExemptDestinations
